import type { CompletedCasesReading } from "../../../contracts/completed-cases.js";
import type { LiveCounter } from "./live-counter";

const qualities = ["GOOD", "STALE", "COUNTER_RESET", "BASELINE_MISSING", "GAP_ACROSS_RUN", "NO_DATA"];
const integer = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 0xffffffff;
const timestamp = (value: unknown): value is string => typeof value === "string" && Number.isFinite(Date.parse(value));
const quality = (value: unknown): value is CompletedCasesReading["quality"] => typeof value === "string" && qualities.includes(value);

/** Validate server/cache data before it can become an operator quantity. */
export function readCompletedCases(value: unknown): CompletedCasesReading | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (!("rawValue" in value) || !("runValue" in value) || !("measuredAt" in value) || !("baselineAt" in value) || !("quality" in value)) return null;
  if ((value.rawValue !== null && !integer(value.rawValue)) || (value.runValue !== null && !integer(value.runValue)) ||
      (value.measuredAt !== null && !timestamp(value.measuredAt)) || (value.baselineAt !== null && !timestamp(value.baselineAt)) || !quality(value.quality)) return null;
  if (value.runValue !== null && (!timestamp(value.baselineAt) || !timestamp(value.measuredAt) || Date.parse(value.baselineAt) > Date.parse(value.measuredAt))) return null;
  return { rawValue: value.rawValue, runValue: value.runValue, measuredAt: value.measuredAt, baselineAt: value.baselineAt, quality: value.quality };
}

export function isFreshCompletedCases(counter: LiveCounter | null, now: number, run = false): boolean {
  const cases = readCompletedCases(counter?.completedCases);
  return counter?.configured === true && cases !== null && integer(cases.rawValue) && timestamp(cases.measuredAt) &&
    Math.abs(now - Date.parse(cases.measuredAt)) <= 10_000 && (!run || (cases.quality === "GOOD" && integer(cases.runValue) && timestamp(cases.baselineAt)));
}

export type CompletedCasesPresentation = {
  value: number | null;
  runValue: number | null;
  rawValue: number | null;
  source: "RUN" | "MACHINE";
  retained: boolean;
  label: string;
};

export function completedCasesPresentation(input: {
  current: LiveCounter | null;
  lastRaw: LiveCounter | null;
  lastRun: LiveCounter | null;
  unavailable: boolean;
  failureCode?: string | null;
  now: number;
}): CompletedCasesPresentation {
  const current = readCompletedCases(input.current?.completedCases);
  const raw = readCompletedCases(input.lastRaw?.completedCases);
  const run = readCompletedCases(input.lastRun?.completedCases);
  const source = run?.runValue !== null && run?.runValue !== undefined ? "RUN" : "MACHINE";
  const value = source === "RUN" ? run?.runValue ?? null : raw?.rawValue ?? null;
  const retained = value !== null && (input.unavailable || !isFreshCompletedCases(input.current, input.now, source === "RUN"));
  const labels: Record<CompletedCasesReading["quality"], string> = {
    GOOD: "Tamamlanan koli sayımı güncel",
    STALE: "Koli ölçümü gecikti",
    COUNTER_RESET: "Koli sayacı sıfırlandı; iş toplamı doğrulanamıyor",
    BASELINE_MISSING: "İş başlangıcı doğrulanamadı",
    GAP_ACROSS_RUN: "Koli sayım aralığı doğrulanamadı",
    NO_DATA: "İlk koli ölçümü bekleniyor",
  };
  const measuredAt = source === "RUN" ? run?.measuredAt : raw?.measuredAt;
  const age = measuredAt ? Math.max(0, Math.floor((input.now - Date.parse(measuredAt)) / 1000)) : null;
  const status = input.unavailable ? input.failureCode === "CLIENT_REQUEST_TIMEOUT" ? "Sunucu yanıtı gecikti" : "Sunucuya ulaşılamıyor"
    : current ? labels[current.quality] : "İlk koli ölçümü bekleniyor";
  return { value, source, runValue: run?.runValue ?? null, rawValue: raw?.rawValue ?? null, retained,
    label: retained ? `${status} · Son doğrulanmış miktar${age === null ? "" : ` · ${age} sn önce`}`
      : current && !isFreshCompletedCases(input.current, input.now) ? "Koli ölçümü gecikti" : status };
}
